import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { randomboxApi } from '../../api/randombox.js';
import { ErrorBox } from '../../components/ui.js';
import { ApiError } from '../../api/client.js';

/**
 * 송장입력 — 원본 randombox/order/delivery_excel_check.php + _exec.php.
 *
 * 택배사에서 받은 엑셀을 브라우저에서 읽어(SheetJS), 배송 반영까지 끝나 송장만
 * 기다리는 주문(tracking_flg=1, delivery_reset_flg=1)의 연락처와 대조한다.
 * 일치하는 건만 골라 '배송완료' + 송장번호로 반영한다.
 *
 * 열은 위치가 아니라 첫 줄의 열 이름('주문자 연락처', '송장번호')으로 찾는다.
 * 원본은 9번째 열(index 8)·22번째 열(index 21)로 고정해 읽었는데, 2026-10-20 양식 변경으로
 * 맨 앞 더미 열('A')과 셀 꼬리('|C|I' 등)가 빠지면서 열이 한 칸씩 당겨졌다.
 * 이름으로 찾으면 기존 양식과 신규 양식을 둘 다 읽는다.
 */

const PHONE_HEADER = '주문자 연락처';
const TRACKING_HEADER = '송장번호';

/** 원본 delivery_excel_check_exec.php 의 전화번호 정규화와 같은 규칙. */
function normalizePhone(raw: string): string {
  return raw.replace(/-/g, '').replace(/\|C\|I$/, '');
}

/**
 * 헤더 줄에서 열 이름의 위치를 찾는다. 기존 양식 헤더는 '주문자 연락처|C|I' 처럼
 * 꼬리가 붙어 있어서, '|' 로 나눈 조각 중 하나가 이름과 같으면 그 열로 본다.
 */
function findColumn(header: unknown[], name: string): number {
  return header.findIndex(
    (cell) => cell != null && String(cell).split('|').some((part) => part.trim() === name),
  );
}

interface ParsedRow {
  phone: string;
  tracking: string;
  matched: boolean;
}

export function RbTrackingPage() {
  const [params] = useSearchParams();
  const randomboxId = params.get('randomboxId') ?? '';

  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [dragging, setDragging] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const pending = useQuery({
    queryKey: ['randombox', 'tracking', 'pending', randomboxId],
    queryFn: () => randomboxApi.trackingPending(randomboxId === '' ? undefined : Number(randomboxId)),
    enabled: false,
  });

  const apply = useMutation({
    mutationFn: (updates: { hphone: string; trackingNumber: string }[]) =>
      randomboxApi.applyTracking({
        randomboxId: randomboxId === '' ? undefined : Number(randomboxId),
        updates,
      }),
  });

  const matchedCount = useMemo(() => rows.filter((r) => r.matched).length, [rows]);

  function reset() {
    setFileName('');
    setRows([]);
    setParseError(null);
    apply.reset();
    if (fileInput.current) fileInput.current.value = '';
  }

  async function processFile(file: File) {
    if (!/\.(xlsx|xls)$/i.test(file.name)) {
      setParseError('엑셀 파일(.xlsx, .xls)만 업로드할 수 있습니다.');
      return;
    }
    setParseError(null);
    setFileName(file.name);

    try {
      const pendingResult = await pending.refetch();
      const dbRows = pendingResult.data ?? [];

      // xlsx(SheetJS)는 무거워서 이 화면에 들어올 때만 받도록 동적 import 로 뺐다.
      const { read, utils } = await import('xlsx');
      const buf = await file.arrayBuffer();
      const workbook = read(buf, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]!];
      // rawNumbers: 숫자로 저장된 송장번호 셀이 '3.03368E+11' 처럼 지수 표기로 읽히지 않게 한다.
      const table = utils.sheet_to_json<unknown[]>(sheet!, { header: 1, raw: false, rawNumbers: true });

      const header = Array.isArray(table[0]) ? table[0] : [];
      const phoneCol = findColumn(header, PHONE_HEADER);
      const trackingCol = findColumn(header, TRACKING_HEADER);
      if (phoneCol < 0 || trackingCol < 0) {
        setRows([]);
        setParseError(
          `엑셀 첫 줄에서 '${PHONE_HEADER}' · '${TRACKING_HEADER}' 열을 찾지 못했습니다. 송장입력 양식이 맞는지 확인해주세요.`,
        );
        return;
      }

      const parsed: ParsedRow[] = [];
      // 원본과 같이 첫 줄(헤더)은 건너뛴다.
      table.forEach((line, idx) => {
        if (idx === 0 || !Array.isArray(line)) return;
        const phone = line[phoneCol] != null ? String(line[phoneCol]).trim() : '';
        const tracking = line[trackingCol] != null ? String(line[trackingCol]).trim() : '';
        if (!phone || !tracking) return;

        const normalized = normalizePhone(phone);
        const matched = dbRows.some((db) => normalizePhone(db.hphone) === normalized);
        parsed.push({ phone, tracking, matched });
      });

      setRows(parsed);
      if (parsed.length === 0) setParseError('유효한 데이터가 없습니다.');
    } catch {
      setRows([]);
      setParseError('데이터 조회 및 엑셀 파싱 중 오류가 발생했습니다.');
    }
  }

  function submit() {
    const updates = rows
      .filter((r) => r.matched)
      .map((r) => ({ hphone: r.phone, trackingNumber: r.tracking }));
    if (updates.length === 0) return;
    apply.mutate(updates);
  }

  return (
    <section>
      <header className="page-head">
        <div>
          <Link className="link link--back" to="/randombox/orders">
            ← 랜덤박스 주문 관리
          </Link>
          <h1 className="page-title">송장입력</h1>
          <p className="page-desc">
            택배사 엑셀을 올리면 송장 대기 중인 주문과 연락처로 대조합니다. 일치하는 건만{' '}
            <strong>배송완료</strong>로 바뀌고 송장번호가 들어갑니다.
            {randomboxId && <> (랜덤박스 ID {randomboxId} 로 좁혀 처리)</>}
          </p>
        </div>
        <div className="page-head__actions">
          <button type="button" className="btn btn--ghost btn--sm" onClick={reset}>
            초기화
          </button>
        </div>
      </header>

      <div
        className={dragging ? 'drop-zone drop-zone--active' : 'drop-zone'}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={(e) => { e.preventDefault(); setDragging(false); }}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          const f = e.dataTransfer.files[0];
          if (f) void processFile(f);
        }}
        onClick={() => fileInput.current?.click()}
      >
        <input
          ref={fileInput}
          type="file"
          accept=".xlsx,.xls"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void processFile(f);
          }}
        />
        <span className="drop-zone__msg">
          {fileName ? fileName : '엑셀 파일을 이곳에 드래그하거나 클릭하세요.'}
        </span>
      </div>

      {parseError && <ErrorBox error={new Error(parseError)} />}
      {apply.isError && (
        <ErrorBox error={apply.error instanceof ApiError ? apply.error : new Error('처리에 실패했습니다.')} />
      )}
      {apply.isSuccess && (
        <p className="ok-note">
          {apply.data.updatedCount}건 처리가 완료되었습니다 (요청 {apply.data.totalUpdates}건).
        </p>
      )}

      {rows.length > 0 && (
        <>
          <p className="hint">
            일치 <strong style={{ color: 'var(--ok)' }}>{matchedCount}건</strong> / 불일치{' '}
            <strong style={{ color: 'var(--danger)' }}>{rows.length - matchedCount}건</strong>
          </p>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>No</th>
                  <th>연락처(엑셀)</th>
                  <th>송장번호(엑셀)</th>
                  <th>상태</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td>{r.phone}</td>
                    <td><code className="code">{r.tracking}</code></td>
                    <td>
                      {r.matched ? (
                        <span className="badge">OK</span>
                      ) : (
                        <span className="badge badge--danger">NG</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {matchedCount > 0 && (
            <button
              type="button"
              className="btn btn--primary btn--block"
              style={{ marginTop: 14 }}
              disabled={apply.isPending}
              onClick={submit}
            >
              {apply.isPending ? '처리 중…' : `${matchedCount}건 송장번호 반영`}
            </button>
          )}
        </>
      )}
    </section>
  );
}
